import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiFacebook, FiTwitter, FiInstagram, FiYoutube, FiMapPin, FiPhone, FiMail, FiClock, FiArrowRight, FiSend } from "react-icons/fi";
import { MdSchool } from "react-icons/md";
import { useSiteSettings } from "../../context/SiteSettingsContext";

const quickLinks = [
  { label: "About Us",      path: "/about" },
  { label: "Academics",     path: "/academics" },
  { label: "Admissions",    path: "/admissions" },
  { label: "Student Life",  path: "/student-life" },
  { label: "News & Events", path: "/news-events" },
  { label: "Gallery",       path: "/gallery" },
  { label: "Notices",       path: "/notices" },
  { label: "Results",       path: "/results" },
  { label: "Contact",       path: "/contact" },
];

const programLinks = [
  { label: "Primary School (1–5)",    path: "/academics#primary" },
  { label: "Secondary School (6–10)", path: "/academics#secondary" },
  { label: "High School (11–12)",     path: "/academics#highschool" },
  { label: "Apply Online",            path: "/admissions/apply" },
  { label: "Scholarships",            path: "/admissions#scholarships" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const { settings } = useSiteSettings();

  return (
    <footer className="bg-[#172554] text-white border-t-4 border-[#2563EB]">
      <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16 py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">

          {/* School info */}
          <div>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-[#2563EB] rounded-2xl flex items-center justify-center shadow-lg shrink-0">
                <MdSchool className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg uppercase tracking-wider leading-none mb-1">
                  {settings?.schoolName ? settings.schoolName.split(' ')[0] : "Early"}<br />
                  <span className="text-sky-300">{settings?.schoolName ? settings.schoolName.substring(settings.schoolName.indexOf(' ') + 1) : "Boarding."}</span>
                </h3>
                <p className="text-blue-300/50 text-[10px] tracking-[0.2em] uppercase font-semibold">
                  {settings?.schoolTagline || 'Inspiring Excellence'}
                </p>
              </div>
            </div>
            <p className="text-blue-200/60 text-sm leading-relaxed mb-8 font-medium">
              {settings?.schoolDescription || 'Early Boarding School & Montessori has been nurturing young minds with quality education since 1999.'}
            </p>
            <div className="flex items-center gap-3">
              {[
                { icon: FiFacebook,  href: settings?.socialMedia?.facebook  || '#', label: 'Facebook' },
                { icon: FiTwitter,   href: settings?.socialMedia?.twitter   || '#', label: 'Twitter' },
                { icon: FiInstagram, href: settings?.socialMedia?.instagram || '#', label: 'Instagram' },
                { icon: FiYoutube,   href: settings?.socialMedia?.youtube   || '#', label: 'YouTube' },
              ].map((s, i) => (
                <a key={i} href={s.href} aria-label={s.label}
                  target={s.href !== '#' ? '_blank' : undefined} rel={s.href !== '#' ? 'noreferrer noopener' : undefined}
                  className="w-9 h-9 bg-blue-900/50 border border-blue-700/50 rounded-xl hover:bg-[#2563EB] hover:border-[#2563EB] flex items-center justify-center transition-all text-blue-300 hover:text-white">
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-white uppercase tracking-wider text-sm mb-7 pb-3 border-b border-blue-800/50">Quick Links</h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  <Link to={link.path}
                    className="flex items-center gap-3 text-blue-200/60 hover:text-sky-300 transition-colors text-sm font-medium group">
                    <FiArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Programs + Newsletter */}
          <div>
            <h4 className="font-bold text-white uppercase tracking-wider text-sm mb-7 pb-3 border-b border-blue-800/50">Programs</h4>
            <ul className="space-y-2.5 mb-10">
              {programLinks.map((link, i) => (
                <li key={i}>
                  <Link to={link.path}
                    className="flex items-center gap-3 text-blue-200/60 hover:text-sky-300 transition-colors text-sm font-medium group">
                    <FiArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="font-bold text-white uppercase tracking-wider text-sm mb-5">Newsletter</h4>
            <div className="flex rounded-xl overflow-hidden border border-blue-700/50 bg-blue-900/30">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="flex-1 min-w-0 px-4 py-3 bg-transparent text-white placeholder-blue-500 text-sm focus:outline-none" />
              <button className="px-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold transition-colors shrink-0">
                <FiSend className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-white uppercase tracking-wider text-sm mb-7 pb-3 border-b border-blue-800/50">Contact Us</h4>
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-900/50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                  <FiMapPin className="w-4 h-4 text-sky-400" />
                </div>
                <p className="text-blue-200/60 text-sm leading-relaxed">
                  {settings?.address?.street ? <>{settings.address.street}<br /></> : null}
                  {settings?.address?.city || 'Kathmandu'}, {settings?.address?.country || 'Nepal'}{settings?.address?.zipCode ? ` ${settings.address.zipCode}` : ''}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-900/50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                  <FiPhone className="w-4 h-4 text-sky-400" />
                </div>
                <div>
                  {settings?.phone?.primary && <a href={`tel:${settings.phone.primary}`} className="text-blue-200/60 hover:text-white transition-colors text-sm block mb-1">{settings.phone.primary}</a>}
                  {settings?.phone?.secondary && <a href={`tel:${settings.phone.secondary}`} className="text-blue-200/60 hover:text-white transition-colors text-sm block">{settings.phone.secondary}</a>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-900/50 rounded-lg flex items-center justify-center shrink-0">
                  <FiMail className="w-4 h-4 text-sky-400" />
                </div>
                <a href={`mailto:${settings?.email || 'info@example.com'}`} className="text-blue-200/60 hover:text-white transition-colors text-sm">
                  {settings?.email || 'info@example.com'}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-900/50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                  <FiClock className="w-4 h-4 text-sky-400" />
                </div>
                <div className="text-blue-200/60 text-sm leading-relaxed">
                  <p className="mb-1">{settings?.officeHours?.monday_friday || 'Sun – Fri: 7:00 AM – 5:00 PM'}</p>
                  <p>{settings?.officeHours?.saturday || 'Saturday: 9:00 AM – 1:00 PM'}</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom bar */}
      <div className="bg-[#0f172a] border-t border-blue-900/50">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16 py-5 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-blue-500/60 text-[10px] font-semibold uppercase tracking-widest">
            &copy; {new Date().getFullYear()} {settings?.schoolName || "Early Boarding School & Montessori"}. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-[10px] font-semibold uppercase tracking-widest text-blue-500/60">
            <Link to="/about"   className="hover:text-sky-400 transition-colors">Privacy Policy</Link>
            <span className="w-1 h-1 bg-blue-800 rounded-full" />
            <Link to="/about"   className="hover:text-sky-400 transition-colors">Terms of Use</Link>
            <span className="w-1 h-1 bg-blue-800 rounded-full" />
            <Link to="/contact" className="hover:text-sky-400 transition-colors">Support</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
